import type { Employee as FrontendEmployee } from '@/types/employee';
import type { Employee as ModelsEmployee } from '@gokrew/models/employee';
import type { CompanyRole } from '@/types/roles';
import type { CompanyAdmin } from '@/types/company-admin';
import {
  type CompanyLeavePolicy,
  type DirectGrantLeaveItem,
  PolicyScope,
  AllocationType,
} from '@/types/leave-policies';
import { Language, Gender } from '@gokrew/models/user';
import { EmployeeNoteVisibility } from '@gokrew/models/employee-notes';
import {
  CompanyLeaveStatus,
  type CompanyLeaveRequestItem,
} from '@gokrew/models/leave-management';
import type { Holiday } from '@/types/holidays';
import type { CalendarEvent } from '@gokrew/ui/types/leave-calendar';
import {
  TimesheetApprovalStatus,
  type TimesheetEmployee,
} from '@/types/timesheet';
import type { DraftLeaveEntitlement } from '@/utility/entitlement';
import type { FileTypes, FolderTypes } from '@/types/files';
import { FileShareScope } from '@gokrew/models/file';
import type { NoteTypes } from '@/types/notes';
import type { Announcement } from '@gokrew/models/announcements';
import type {
  AssetType,
  AssetTypeResolvedField,
  Field,
  FieldSection,
  FieldSet,
} from '@gokrew/models/asset-type';
import type { AssetModel } from '@gokrew/models/asset-model';
import type { AssignedTaskList } from '@gokrew/models/employee-boarding';
import type { BenefitPlan } from '@gokrew/models/benefitPlans';
import type { MyAdminTask } from '@gokrew/models/tasks';

/**
 * All mock data below is display-only sample content injected during a Joyride
 * tour. It's built from `t` (TranslationNS.Tour, "mock.*" keys) rather than
 * hardcoded strings so the tour reads correctly in every supported locale.
 */
export type TourMockT = (
  key: string,
  options?: Record<string, unknown>
) => string;

export const getMockDepartment = (t: TourMockT) => ({
  id: '__tour_mock__',
  name: t('mock.department.name'),
  description: t('mock.department.description'),
  status: true,
  employeesCount: 12,
  companyId: '__tour_mock__',
  branchIds: [],
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  deletedAt: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  createdByUser: {
    id: '__tour_mock__',
    firstName: 'Ashish',
    lastName: 'Raj',
  },
});

export const getMockDesignation = (t: TourMockT) => ({
  id: '__tour_mock__',
  name: t('mock.designation.product_designer'),
  description: t('mock.designation.product_designer_description'),
  status: true,
  employeesCount: 4,
  companyId: '__tour_mock__',
  branchIds: [],
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  deletedAt: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  createdByUser: {
    id: '__tour_mock__',
    firstName: 'Ashish',
    lastName: 'Raj',
  },
});

export const getMockRole = (t: TourMockT): CompanyRole => ({
  id: '__tour_mock__',
  companyId: '__tour_mock__',
  name: t('mock.role.name'),
  description: t('mock.role.description'),
  status: true,
  default: false,
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
  associatedUser: 6,
  assignedPermissionIds: [],
  createdByUser: {
    id: '__tour_mock__',
    firstName: 'Ashish',
    lastName: 'Raj',
  },
});

// Shared base — untyped so both Employee variants can spread from it cleanly.
const getMockEmployeeBase = (t: TourMockT) => {
  const designation = getMockDesignation(t);
  const department = getMockDepartment(t);
  return {
    id: '__tour_mock__',
    userId: '__tour_mock__',
    designationId: designation.id,
    departmentId: department.id,
    companyId: '__tour_mock__',
    branchId: '__tour_mock__',
    employeeReferenceId: 'EMP-001',
    status: true,
    dateOfJoining: new Date('2024-03-01'),
    employmentType: 'full_time',
    workEmail: 'alex.morgan@kollabhr.com',
    workPhone: null,
    terminatedAt: null,
    createdByUserId: '__tour_mock__',
    updatedByUserId: null,
    createdAt: '2024-03-01T00:00:00.000Z',
    updatedAt: '2024-03-01T00:00:00.000Z',
    deletedAt: null,
    statusMeta: {
      color: 'green' as const,
      meaning: 'activeAndLoggedInUser' as const,
    },
    user: {
      id: '__tour_mock__',
      firstName: 'Alex',
      lastName: 'Morgan',
      email: 'alex.morgan@kollabhr.com',
      profileImage: null,
      dateOfBirth: undefined,
      language: Language.EN,
      gender: Gender.PREFER_NOT_TO_SAY,
      phoneCountryCode: null,
      phoneNumber: null,
      phoneVerifiedAt: null,
      temporaryPasswordGeneratedAt: '2024-03-01T00:00:00.000Z',
      passwordChangedAt: '2024-03-01T00:00:00.000Z',
      createdAt: '2024-03-01T00:00:00.000Z',
      updatedAt: '2024-03-01T00:00:00.000Z',
    },
    designation,
    department,
  };
};

// Used in EmployeesPage — typed against the frontend Employee which has extra fields.
export const getMockEmployee = (t: TourMockT): FrontendEmployee => ({
  ...getMockEmployeeBase(t),
  leaveEligibleFrom: undefined,
  contractEndDate: undefined,
  lastInvitationSentAt: null,
});

export const getMockLeaveRequest = (t: TourMockT): CompanyLeaveRequestItem => ({
  id: '__tour_mock__',
  companyId: '__tour_mock__',
  employeeId: '__tour_mock__',
  leaveWalletId: '__tour_mock__',
  status: CompanyLeaveStatus.Pending,
  reason: t('mock.leave_reason.family_vacation'),
  rejectionReason: null,
  cancellationReason: null,
  createdAt: '2024-06-01T00:00:00.000Z',
  approvedAt: '2024-06-01T00:00:00.000Z',
  rejectedAt: null,
  cancelledAt: null,
  approvedByUserId: null,
  rejectedByUserId: null,
  cancelledByUserId: null,
  createdByUserId: '__tour_mock__',
  approvedByUser: { firstName: '', lastName: '' },
  rejectedByUser: { firstName: '', lastName: '' },
  cancelledByUser: null,
  days: 3,
  startDate: '2024-06-10',
  endDate: '2024-06-12',
  startSession: null,
  endSession: null,
  startTime: null,
  endTime: null,
  employee: {
    ...getMockEmployeeBase(t),
    contractEndDate: null,
    branch: { id: '__tour_mock__', name: t('mock.branch.main') },
  } as ModelsEmployee,
  leaveWallet: {
    id: '__tour_mock__',
    leaveType: {
      id: '__tour_mock__',
      name: t('mock.leave_type.annual'),
      description: null,
    },
  },
});

export const getMockPolicies = (t: TourMockT): CompanyLeavePolicy[] => [
  {
    id: '__tour_mock__',
    companyId: '__tour_mock__',
    name: t('mock.policy.name'),
    description: t('mock.policy.description'),
    effectiveStartDate: '2026-01-01T00:00:00.000Z',
    effectiveEndDate: null,
    policyScope: PolicyScope.DepartmentDesignation,
    isActive: true,
    createdByUserId: '__tour_mock__',
    updatedByUserId: '__tour_mock__',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    deletedAt: null,
    departmentDesignationMappings: [
      {
        id: '__tour_mock_ddm_1__',
        companyId: '__tour_mock__',
        departmentId: '__tour_mock_dept__',
        designationId: null,
        leavePolicyId: '__tour_mock__',
        createdByUserId: '__tour_mock__',
        createdAt: '2026-01-01T00:00:00.000Z',
        department: {
          id: '__tour_mock_dept__',
          name: t('mock.department.name'),
          description: '',
          status: true,
          companyId: '__tour_mock__',
          createdByUserId: '__tour_mock__',
          updatedByUserId: null,
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
          deletedAt: null,
        },
        designation: null,
      },
    ],
    employeeMappings: null,
    employeeLeaveMappings: [],
    policyLeaveTypes: [
      {
        id: '__tour_mock_plt__',
        companyId: '__tour_mock__',
        companyLeavePolicyId: '__tour_mock__',
        companyLeaveTypeId: '__tour_mock_lt__',
        allocationType: 'monthly',
        allocationMonth: 6,
        allowance: 3,
        isProRated: false,
        isAutoApproved: false,
        minAdvanceDays: 2,
        allocationDate: 1,
        isCarryForwardEnabled: true,
        carryForwardType: 'max_limit',
        carryForwardMaxDays: 5,
        carryForwardPercentage: null,
        minYearsOfService: null,
        maxYearsOfService: null,
        minMonthsOfService: null,
        maxMonthsOfService: null,
        createdByUserId: '__tour_mock__',
        updatedByUserId: '__tour_mock__',
        createdAt: '2026-01-01T00:00:00.000Z',
        updatedAt: '2026-01-01T00:00:00.000Z',
        deletedAt: null,
        companyLeaveType: {
          id: '__tour_mock_lt__',
          companyId: '__tour_mock__',
          name: t('mock.leave_type.paid'),
          description: '',
          createdByUserId: '__tour_mock__',
          updatedByUserId: null,
          createdAt: '2026-01-01T00:00:00.000Z',
          updatedAt: '2026-01-01T00:00:00.000Z',
        },
      },
    ],
  } as unknown as CompanyLeavePolicy,
];

export const getMockCompanyAdmin = (t: TourMockT): CompanyAdmin => ({
  id: '__tour_mock__',
  userId: '__tour_mock__',
  status: true,
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  lastInvitationSentAt: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
  companyRoleId: '__tour_mock__',
  branchIds: [],
  extraData: {},
  sendInvitationMail: false,
  user: {
    firstName: 'Meera',
    lastName: 'Nair',
    email: 'meera.nair@kollabhr.com',
    dateOfBirth: undefined,
    profileImage: null,
    language: Language.EN,
    gender: Gender.PREFER_NOT_TO_SAY,
  },
  mapping: {
    id: '__tour_mock__',
    companyRole: {
      id: '__tour_mock__',
      name: t('mock.company_admin.role'),
      description: null,
      createdAt: '2024-03-01T00:00:00.000Z',
      status: true,
      default: false,
      updatedAt: null,
      deletedAt: null,
      createdByUserId: '__tour_mock__',
      updatedByUserId: null,
    },
  },
  branches: [],
  createdByUser: { id: '__tour_mock__', firstName: 'Ashish', lastName: 'Raj' },
  statusMeta: {
    color: 'green' as const,
    meaning: 'activeAndLoggedInUser' as const,
  },
});

// Used in DepartmentDetailsPage — typed against the models Employee.
export const getMockDeptDetailEmployee = (t: TourMockT): ModelsEmployee => ({
  ...getMockEmployeeBase(t),
  contractEndDate: null,
  branch: { id: '__tour_mock__', name: t('mock.branch.main') },
});

export const getMockLeaveWalletTypes = (t: TourMockT) => [
  {
    id: '__tour_mock_lt_paid__',
    companyId: '__tour_mock__',
    name: t('mock.leave_type.paid'),
    description: null,
    createdByUserId: '__tour_mock__',
    updatedByUserId: null,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: '__tour_mock_lt_sick__',
    companyId: '__tour_mock__',
    name: t('mock.leave_type.sick'),
    description: null,
    createdByUserId: '__tour_mock__',
    updatedByUserId: null,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
];

export const getMockLeaveWallets = (t: TourMockT) => [
  {
    employeeId: '__tour_mock_1__',
    firstName: 'Alex',
    lastName: 'Morgan',
    department: { id: '__tour_mock__', name: t('mock.department.name') },
    designation: {
      id: '__tour_mock__',
      name: t('mock.designation.product_designer'),
    },
    wallets: [
      {
        name: t('mock.leave_type.paid'),
        totalAvailable: 12,
        totalTaken: 3,
        totalAvailableInHours: 96,
        totalTakenInHours: 24,
        types: [
          {
            available: 12,
            taken: 3,
            availableInHours: 96,
            takenInHours: 24,
            leaveTypeId: '__tour_mock_lt_paid__',
            leaveTypeName: t('mock.leave_type.paid'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
      {
        name: t('mock.leave_type.sick'),
        totalAvailable: 6,
        totalTaken: 1,
        totalAvailableInHours: 48,
        totalTakenInHours: 8,
        types: [
          {
            available: 6,
            taken: 1,
            availableInHours: 48,
            takenInHours: 8,
            leaveTypeId: '__tour_mock_lt_sick__',
            leaveTypeName: t('mock.leave_type.sick'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
    ],
  },
  {
    employeeId: '__tour_mock_2__',
    firstName: 'Priya',
    lastName: 'Sharma',
    department: { id: '__tour_mock__', name: t('mock.department.name') },
    designation: {
      id: '__tour_mock__',
      name: t('mock.designation.frontend_engineer'),
    },
    wallets: [
      {
        name: t('mock.leave_type.paid'),
        totalAvailable: 15,
        totalTaken: 5,
        totalAvailableInHours: 120,
        totalTakenInHours: 40,
        types: [
          {
            available: 15,
            taken: 5,
            availableInHours: 120,
            takenInHours: 40,
            leaveTypeId: '__tour_mock_lt_paid__',
            leaveTypeName: t('mock.leave_type.paid'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
      {
        name: t('mock.leave_type.sick'),
        totalAvailable: 6,
        totalTaken: 0,
        totalAvailableInHours: 48,
        totalTakenInHours: 0,
        types: [
          {
            available: 6,
            taken: 0,
            availableInHours: 48,
            takenInHours: 0,
            leaveTypeId: '__tour_mock_lt_sick__',
            leaveTypeName: t('mock.leave_type.sick'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
    ],
  },
  {
    employeeId: '__tour_mock_3__',
    firstName: 'James',
    lastName: 'Okafor',
    department: { id: '__tour_mock_ops__', name: t('mock.branch.operations') },
    designation: {
      id: '__tour_mock__',
      name: t('mock.designation.hr_manager'),
    },
    wallets: [
      {
        name: t('mock.leave_type.paid'),
        totalAvailable: 18,
        totalTaken: 10,
        totalAvailableInHours: 144,
        totalTakenInHours: 80,
        types: [
          {
            available: 18,
            taken: 10,
            availableInHours: 144,
            takenInHours: 80,
            leaveTypeId: '__tour_mock_lt_paid__',
            leaveTypeName: t('mock.leave_type.paid'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
      {
        name: t('mock.leave_type.sick'),
        totalAvailable: 6,
        totalTaken: 3,
        totalAvailableInHours: 48,
        totalTakenInHours: 24,
        types: [
          {
            available: 6,
            taken: 3,
            availableInHours: 48,
            takenInHours: 24,
            leaveTypeId: '__tour_mock_lt_sick__',
            leaveTypeName: t('mock.leave_type.sick'),
            leavePolicyId: '__tour_mock_policy__',
            leavePolicyName: t('mock.policy.name'),
          },
        ],
      },
    ],
  },
];

export const getMockCalendarEvents = (t: TourMockT): CalendarEvent[] => [
  {
    id: '__tour_mock_cal_1__',
    start: '2026-07-01',
    extendedProps: {
      label: t('mock.calendar_label', {
        name: 'Alex Morgan',
        leaveType: t('mock.leave_type.paid'),
      }),
      type: 'leave',
      leave: {
        leaveRequestId: '__tour_mock__',
        startDate: '2026-07-01',
        endDate: '2026-07-01',
        status: 'approved',
        reason: t('mock.leave_reason.vacation'),
        leaveType: { id: '__tour_mock__', name: t('mock.leave_type.paid') },
        employee: {
          id: '__tour_mock__',
          firstName: 'Alex',
          lastName: 'Morgan',
          email: 'alex@kollabhr.com',
          profileImage: null,
        },
      },
    },
  },
  {
    id: '__tour_mock_cal_2__',
    start: '2026-07-01',
    extendedProps: {
      label: t('mock.calendar_label', {
        name: 'Priya Sharma',
        leaveType: t('mock.leave_type.sick'),
      }),
      type: 'leave',
      leave: {
        leaveRequestId: '__tour_mock_2__',
        startDate: '2026-07-01',
        endDate: '2026-07-01',
        status: 'approved',
        reason: t('mock.leave_reason.unwell'),
        leaveType: { id: '__tour_mock__', name: t('mock.leave_type.sick') },
        employee: {
          id: '__tour_mock_2__',
          firstName: 'Priya',
          lastName: 'Sharma',
          email: 'priya@kollabhr.com',
          profileImage: null,
        },
      },
    },
  },
];

export const getMockCalendarLegends = (t: TourMockT) => [
  {
    label: t('mock.leave_type.paid'),
    className: 'bg-indigo-100 text-indigo-600',
  },
  {
    label: t('mock.leave_type.sick'),
    className: 'bg-yellow-100 text-yellow-600',
  },
];

export const getMockHolidays = (t: TourMockT): Holiday[] => [
  {
    id: '__tour_mock_holiday_1__',
    name: t('mock.holiday.new_year'),
    leaveDate: '2026-01-01',
    description: undefined,
    companyId: '__tour_mock__',
    leaveScheduleId: '__tour_mock__',
    createdByUserId: '__tour_mock__',
    updatedByUserId: null,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: '__tour_mock_holiday_2__',
    name: t('mock.holiday.republic_day'),
    leaveDate: '2026-01-26',
    description: undefined,
    companyId: '__tour_mock__',
    leaveScheduleId: '__tour_mock__',
    createdByUserId: '__tour_mock__',
    updatedByUserId: null,
    createdAt: '2026-01-26T00:00:00.000Z',
    updatedAt: '2026-01-26T00:00:00.000Z',
  },
];

export const getMockTimesheetEmployees = (
  t: TourMockT
): TimesheetEmployee[] => [
  {
    id: '__tour_mock_ts_1__',
    weekApprovalId: '__tour_mock_ts_wa_1__',
    initials: 'AM',
    name: 'Alex Morgan',
    role: t('mock.designation.product_designer'),
    approvalStatus: TimesheetApprovalStatus.Approved,
    totalHours: 38.5,
    targetHours: 40,
    weeklyTargetHours: 40,
    missingDays: 0,
    entries: {
      mon: { status: 'full', hours: '8.0' },
      tue: { status: 'full', hours: '8.0' },
      wed: { status: 'partial', hours: '5.5' },
      thu: { status: 'full', hours: '8.0' },
      fri: { status: 'full', hours: '9.0' },
      sat: { status: 'weekly_off' },
      sun: { status: 'weekly_off' },
    },
  },
  {
    id: '__tour_mock_ts_2__',
    weekApprovalId: '__tour_mock_ts_wa_2__',
    initials: 'PS',
    name: 'Priya Sharma',
    role: t('mock.designation.frontend_engineer'),
    approvalStatus: TimesheetApprovalStatus.Pending,
    totalHours: 32,
    targetHours: 40,
    weeklyTargetHours: 40,
    missingDays: 0,
    entries: {
      mon: { status: 'full', hours: '8.0' },
      tue: { status: 'leave_full' },
      wed: { status: 'leave_full' },
      thu: { status: 'full', hours: '8.0' },
      fri: { status: 'full', hours: '8.0' },
      sat: { status: 'weekly_off' },
      sun: { status: 'weekly_off' },
    },
  },
  {
    id: '__tour_mock_ts_3__',
    weekApprovalId: null,
    initials: 'JO',
    name: 'James Okafor',
    role: t('mock.designation.hr_manager'),
    approvalStatus: TimesheetApprovalStatus.Pending,
    totalHours: 28,
    targetHours: 40,
    weeklyTargetHours: 40,
    missingDays: 1,
    entries: {
      mon: { status: 'missing' },
      tue: { status: 'full', hours: '8.0' },
      wed: { status: 'partial', hours: '4.0' },
      thu: { status: 'full', hours: '8.0' },
      fri: { status: 'full', hours: '8.0' },
      sat: { status: 'weekly_off' },
      sun: { status: 'weekly_off' },
    },
  },
];

export const getMockGrantLeaveHistory = (
  t: TourMockT
): DirectGrantLeaveItem[] => [
  {
    id: '__tour_mock_grant_1__',
    amount: 5,
    remark: t('mock.grant_remark.bonus'),
    createdAt: '2026-06-23T00:00:00.000Z',
    grantedToEmployee: {
      id: '__tour_mock_1__',
      user: { firstName: 'Alex', lastName: 'Morgan' },
    },
    grantedByUser: { firstName: 'Ashu', lastName: 'Sharma' },
    policyLeaveType: {
      id: '__tour_mock_plt__',
      companyLeaveType: { name: t('mock.leave_type.sick') },
    },
  },
  {
    id: '__tour_mock_grant_2__',
    amount: 3,
    remark: t('mock.grant_remark.project'),
    createdAt: '2026-06-18T00:00:00.000Z',
    grantedToEmployee: {
      id: '__tour_mock_2__',
      user: { firstName: 'Priya', lastName: 'Sharma' },
    },
    grantedByUser: { firstName: 'Ashish', lastName: 'Raj' },
    policyLeaveType: {
      id: '__tour_mock_plt__',
      companyLeaveType: { name: t('mock.leave_type.casual') },
    },
  },
  {
    id: '__tour_mock_grant_3__',
    amount: 2,
    remark: null,
    createdAt: '2026-06-10T00:00:00.000Z',
    grantedToEmployee: {
      id: '__tour_mock_3__',
      user: { firstName: 'James', lastName: 'Okafor' },
    },
    grantedByUser: { firstName: 'Ashu', lastName: 'Sharma' },
    policyLeaveType: {
      id: '__tour_mock_plt__',
      companyLeaveType: { name: t('mock.leave_type.vacation') },
    },
  },
];

export const getMockEntitlement = (t: TourMockT): DraftLeaveEntitlement => ({
  tempId: 'TEMP_ID_tour_mock',
  leaveType: t('mock.leave_type.paid'),
  allocationType: AllocationType.Monthly,
  allocationMonth: '1',
  allocationDate: 1,
  allowance: 1.5,
  proRata: false,
  isAutoApproved: true,
  minAdvanceDays: 2,
});

export const MOCK_COMPANY_TASK = {
  id: '__tour_mock_company_task__',
  companyId: '__tour_mock_company__',
  title: 'Quarterly Compliance Training',
  description: 'Complete the mandatory compliance training module',
  priority: 'high',
  requiresApproval: true,
  scheduleType: 'one_time',
  assignmentMode: 'group',
  assignToAllEmployees: false,
  assignToAllAdmins: false,
  assignDate: '2024-03-01',
  dueDate: '2024-03-15',
  recurrenceRule: null,
  assignees: [],
  createdByUserId: '__tour_mock_user__',
  updatedByUserId: '__tour_mock_user__',
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  assigneeCount: 12,
  deletedAt: null,
  currentOccurrence: null,
  attachments: [],
  occurrences: [],
  createdBy: {
    id: '__tour_mock_user__',
    firstName: 'Jordan',
    lastName: 'Lee',
    email: 'jordan.lee@example.com',
  },
};

export const getMockTaskList = (t: TourMockT) => ({
  id: '__tour_mock__',
  name: t('mock.task_list.onboarding_name'),
  description: t('mock.task_list.onboarding_description'),
  tasksCount: 8,
  isActive: true,
  category: 'onboarding',
  updatedAt: '2024-03-01T00:00:00.000Z',
});

export const getMockOffboardingTaskList = (t: TourMockT) => ({
  id: '__tour_mock_offboarding__',
  name: t('mock.task_list.offboarding_name'),
  description: t('mock.task_list.offboarding_description'),
  tasksCount: 6,
  isActive: true,
  category: 'offboarding',
  updatedAt: '2024-03-01T00:00:00.000Z',
});

export const MOCK_CHILD_TASK = {
  id: '__tour_mock_child_task__',
  name: 'Verify offer letter signature',
  assigneeType: 'admin',
  targetId: null,
  dependentTaskId: '__tour_mock_task__',
  dueDateType: 'after',
  dueDateOffset: 1,
  orderIndex: 0,
  children: [],
};

export const MOCK_TASK = {
  id: '__tour_mock_task__',
  name: 'Collect signed offer letter',
  assigneeType: 'employee_dynamic',
  targetId: null,
  dependentTaskId: null,
  dueDateType: 'on',
  dueDateOffset: 0,
  orderIndex: 0,
  children: [MOCK_CHILD_TASK],
};

export const MOCK_ASSIGNED_TASK: MyAdminTask = {
  assignmentId: '__tour_mock_assigned_task__',
  taskId: '__tour_mock_task__',
  occurrenceId: '__tour_mock_occurrence__',
  threadId: null,
  title: 'Submit signed offer letter',
  description: null,
  priority: 'medium',
  requiresApproval: false,
  scheduleType: 'one_time',
  assignmentMode: 'individual',
  assignDate: '2024-03-25T00:00:00.000Z',
  dueDate: '2024-04-01T00:00:00.000Z',
  status: 'in_progress',
  submittedAt: null,
  completedAt: null,
  approvedAt: null,
  isOverdue: false,
  lastUpdated: '2024-03-28T00:00:00.000Z',
  assignedBy: {
    id: '__tour_mock_user__',
    name: 'Jordan Lee',
    role: 'Company Admin',
  },
  source: 'flow',
  flowType: 'onboarding',
  flowEmployee: { id: '__tour_mock__', name: 'Alex Morgan' },
};

export const MOCK_OFFBOARDING_TASK: MyAdminTask = {
  ...MOCK_ASSIGNED_TASK,
  assignmentId: '__tour_mock_offboarding_task__',
  taskId: '__tour_mock_offboarding_task_def__',
  title: 'Return company laptop',
  flowType: 'offboarding',
};

export const MOCK_SINGLE_TASK: MyAdminTask = {
  assignmentId: '__tour_mock_single_task__',
  taskId: '__tour_mock_single_task_def__',
  occurrenceId: '__tour_mock_single_occurrence__',
  threadId: null,
  title: 'Renew office WiFi contract',
  description: null,
  priority: 'high',
  requiresApproval: false,
  scheduleType: 'one_time',
  assignmentMode: 'group',
  assignDate: '2024-04-20T00:00:00.000Z',
  dueDate: '2024-05-01T00:00:00.000Z',
  status: 'not_started',
  submittedAt: null,
  completedAt: null,
  approvedAt: null,
  isOverdue: false,
  lastUpdated: '2024-04-20T00:00:00.000Z',
  assignedBy: {
    id: '__tour_mock_user__',
    name: 'Jordan Lee',
    role: 'Company Admin',
  },
  source: 'company',
  flowType: null,
  flowEmployee: null,
};
export const MOCK_FOLDER: FolderTypes = {
  id: '__tour_mock__',
  name: 'Company Documents',
  parentId: null,
  icon: undefined,
  color: undefined,
  fileCount: 3,
  subFolderCount: 1,
  type: 'folder',
  children: [
    {
      id: '__tour_mock_child__',
      name: 'Policies',
      parentId: '__tour_mock__',
      fileCount: 2,
      createdByUserId: '__tour_mock__',
      updatedByUserId: null,
      createdAt: '2024-03-01T00:00:00.000Z',
      updatedAt: '2024-03-01T00:00:00.000Z',
      deletedAt: null,
    },
  ],
  companyId: '__tour_mock__',
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
  deletedWithFolderId: null,
};

export const MOCK_FILE: FileTypes = {
  id: '__tour_mock__',
  name: 'Employee Handbook.pdf',
  urlKey: 'company/__tour_mock__/Employee_Handbook.pdf',
  mimeType: 'application/pdf',
  folderId: '__tour_mock__',
  size: '2097152',
  shareScope: FileShareScope.ALL,
  netEmployeeCount: 0,
  type: 'file',
  parent: { id: '__tour_mock__', name: 'Documents' },
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
  createdByUser: { id: '__tour_mock__', firstName: 'Alex', lastName: 'Morgan' },
};

// ─── Vendors ─────────────────────────────────────────────────────────────────

export const getMockVendor = (t: TourMockT) => ({
  id: '__tour_mock__',
  companyId: '__tour_mock__',
  name: t('mock.vendor.name'),
  email: t('mock.vendor.email'),
  phoneNumber: '+1 (555) 000-1234',
  websiteUrl: t('mock.vendor.website'),
  status: 'active' as const,
  createdByUserId: '__tour_mock__',
  updatedByUserId: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
});

// ─── Branches ────────────────────────────────────────────────────────────────

export const getMockBranch = (t: TourMockT) => ({
  id: '__tour_mock__',
  companyId: '__tour_mock__',
  name: t('mock.branch.main'),
  phoneNumber: 0,
  email: '',
  branchCode: '',
  addressLine1: '123 Main Street',
  addressLine2: '',
  city: 'Mumbai',
  postalCode: 400001,
  countryCode: 'IN',
  provinceId: null,
  timeZone: 'Asia/Kolkata',
  currency: 'INR',
  isDefault: true,
  isActive: true,
  settings: '',
  createdByUserId: '__tour_mock__',
  updatedByUserId: '__tour_mock__',
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: '',
  employeesCount: 12,
  branchAdminAssignments: [],
  createdByUser: {
    id: '__tour_mock__',
    firstName: 'Ashish',
    lastName: 'Raj',
  },
});

// ─── Asset Types ──────────────────────────────────────────────────────────────

/**
 * Sentinel id shared by every asset-type tour mock. It is deliberately not a
 * UUID so `useAssetTypeById` (which is gated by `isValidUUID`) never fires a
 * network request for it — the settings/form pages render mock data instead.
 */
export const TOUR_MOCK_ID = '__tour_mock__';
const FIELD_SET_ID = '__tour_mock_fieldset__';

// A single demo field catalog reused across the settings preview (as
// `FieldSection`/`Field`) and the create-form (as `AssetTypeResolvedField`), so
// the tour can say "this form field comes from the setting you just saw".
const MOCK_FIELDS = (t: TourMockT) => [
  {
    id: '__tour_field_serial__',
    key: 'serial_number',
    formKey: 'serialNumber',
    label: t('mock.asset_type.fields.serial_number'),
    dataType: 'text' as const,
    scope: 'model' as const,
    section: 'identification',
  },
  {
    id: '__tour_field_purchase__',
    key: 'purchase_date',
    formKey: 'purchaseDate',
    label: t('mock.asset_type.fields.purchase_date'),
    dataType: 'date' as const,
    scope: 'model' as const,
    section: 'procurement',
  },
  {
    id: '__tour_field_warranty__',
    key: 'under_warranty',
    formKey: 'underWarranty',
    label: t('mock.asset_type.fields.under_warranty'),
    dataType: 'boolean' as const,
    scope: 'model' as const,
    section: 'procurement',
  },
  {
    id: '__tour_field_condition__',
    key: 'condition',
    formKey: 'condition',
    label: t('mock.asset_type.fields.condition'),
    dataType: 'text' as const,
    scope: 'unit' as const,
    section: 'condition',
  },
];

/** Demo company asset type row for the Asset Types table (empty-state step). */
export const getMockAssetType = (t: TourMockT): AssetType => ({
  id: TOUR_MOCK_ID,
  companyId: TOUR_MOCK_ID,
  name: t('mock.asset_type.name'),
  alias: t('mock.asset_type.name'),
  type: 'company',
  fieldSetId: FIELD_SET_ID,
  fieldSet: {
    id: FIELD_SET_ID,
    name: t('mock.asset_type.field_set'),
    isDefault: false,
  },
  visibility: true,
  isEditable: true,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
});

/** Demo asset row for the Assets table (empty-state step). */
export const getMockAssetModel = (t: TourMockT): AssetModel => ({
  id: TOUR_MOCK_ID,
  companyId: TOUR_MOCK_ID,
  assetTypeId: TOUR_MOCK_ID,
  vendorId: null,
  description: null,
  displayName: t('mock.asset_type.name'),
  assetTag: 'TOUR-ASSET-001',
  modelVariant: null,
  brandId: null,
  brand: null,
  vendor: null,
  assetType: { id: TOUR_MOCK_ID, name: t('mock.asset_type.field_set') },
  purchaseDate: null,
  purchasePrice: null,
  currency: null,
  invoiceNumber: null,
  warrantyDurationMonths: null,
  maintenanceSchedule: null,
  ownershipType: null,
  usageType: null,
  impact: null,
  criticality: null,
  createdByUserId: TOUR_MOCK_ID,
  updatedByUserId: null,
  deletedByUserId: null,
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  deletedAt: null,
  unitsCount: 1,
  assignedUnitsCount: 0,
  inStockUnitsCount: 1,
  files: [],
});

const MOCK_SECTION_META = [
  { id: '__tour_section_identification__', key: 'identification' },
  { id: '__tour_section_procurement__', key: 'procurement' },
  { id: '__tour_section_condition__', key: 'condition' },
];

/**
 * Mock catalog for the settings page when the tour has no real asset type to
 * open. Display-only — the page disables Save while the tour runs.
 */
export const getMockAssetTypeSettings = (t: TourMockT) => {
  const fields = MOCK_FIELDS(t);

  const sections: FieldSection[] = MOCK_SECTION_META.map((meta, i) => ({
    id: meta.id,
    name: t(`mock.asset_type.sections.${meta.key}`),
    sortOrder: i,
    fields: fields
      .filter((f) => f.section === meta.key)
      .map<Field>((f, j) => ({
        id: f.id,
        key: f.key,
        label: f.label,
        dataType: f.dataType,
        scope: f.scope,
        fieldSectionId: meta.id,
        sortOrder: j,
        isSystem: true,
      })),
  }));

  const fieldSets: FieldSet[] = [
    {
      id: FIELD_SET_ID,
      name: t('mock.asset_type.field_set'),
      description: t('mock.asset_type.field_set_description'),
      isDefault: false,
      isSystem: false,
      sortOrder: 0,
      fieldCount: fields.length,
    },
  ];

  return {
    type: getMockAssetType(t),
    sections,
    fieldSets,
    fieldSetId: FIELD_SET_ID,
    // Every demo field is enabled so the preview and the create form line up.
    enabledFieldIds: new Set(fields.map((f) => f.id)),
  };
};

/** Demo resolved fields for the create-form's dynamic sections (empty-state). */
export const getMockResolvedAssetFields = (
  t: TourMockT
): {
  assetFields: AssetTypeResolvedField[];
  variationFields: AssetTypeResolvedField[];
} => {
  const toResolved = (
    f: ReturnType<typeof MOCK_FIELDS>[number],
    i: number
  ): AssetTypeResolvedField => ({
    id: f.id,
    key: f.key,
    formKey: f.formKey,
    label: f.label,
    dataType: f.dataType,
    scope: f.scope,
    sortOrder: i,
    sectionId: `__tour_section_${f.section}__`,
    sectionName: t(`mock.asset_type.sections.${f.section}`),
    sectionKey: f.section,
    dataSource: '',
  });

  const fields = MOCK_FIELDS(t).map(toResolved);
  return {
    assetFields: fields.filter((f) => f.scope === 'model'),
    variationFields: fields.filter((f) => f.scope === 'unit'),
  };
};

export const getMockBoardingTaskList = (t: TourMockT): AssignedTaskList => ({
  id: '__tour_mock_boarding_list__',
  name: t('mock.boarding_task_list.name'),
  orderIndex: 0,
  sourceTaskListTemplateId: '__tour_mock__',
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  tasks: [
    {
      id: '__tour_mock_boarding_task__',
      title: t('mock.boarding_task_list.task_title'),
      description: null,
      priority: 'medium',
      requiresApproval: false,
      sourceTemplateId: null,
      dependentTaskId: null,
      unlockedAt: null,
      isLocked: false,
      orderIndex: 0,
      flowTaskListId: '__tour_mock_boarding_list__',
      occurrenceId: '__tour_mock__',
      assignDate: '2024-04-01T00:00:00.000Z',
      dueDate: '2024-04-15T00:00:00.000Z',
      assignmentId: '__tour_mock__',
      status: 'not_started',
      completedAt: null,
      submittedAt: null,
      approvedAt: null,
      assignee: {
        userId: '__tour_mock__',
        role: 'employee',
        firstName: t('mock.boarding_task_list.assignee_first_name'),
        lastName: t('mock.boarding_task_list.assignee_last_name'),
      },
      createdAt: '2024-03-01T00:00:00.000Z',
      updatedAt: '2024-03-01T00:00:00.000Z',
    },
  ],
});

export const getMockBenefitPlan = (t: TourMockT): BenefitPlan => ({
  id: '__tour_mock__',
  name: t('mock.benefit_plan.name'),
  type: {
    id: '__tour_mock_type__',
    name: t('mock.benefit_plan.type_name'),
    isSystem: true,
    companyId: '__tour_mock__',
    createdAt: '2024-03-01T00:00:00.000Z',
    updatedAt: '2024-03-01T00:00:00.000Z',
  },
  typeId: '__tour_mock_type__',
  country: 'US',
  provider: t('mock.benefit_plan.provider'),
  isActive: true,
  defaultEmployeeContribPercent: 3,
  defaultEmployerMatchPercent: 3,
  defaultMatchCapPercent: 6,
  companyId: '__tour_mock__',
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
});

// ─── Admin Assigned Tasks ─────────────────────────────────────────────────────

export const MOCK_ADMIN_ASSIGNED_TASK = {
  id: '__tour_mock_assigned_task__',
  name: 'Complete onboarding checklist',
  taskListName: 'New Hire Onboarding',
  flowType: 'onboarding' as const,
  status: 'in_progress' as const,
  dueDate: null,
  isOverdue: false,
  daysOverdue: 0,
  isLocked: false,
  dependentTaskId: null,
  employee: { firstName: 'Alex', lastName: 'Johnson' },
};

export const getMockNote = (t: TourMockT): NoteTypes => {
  const mockCategory = {
    id: '__tour_mock_category__',
    createdAt: '2024-03-01T00:00:00.000Z',
    createdBy: '__tour_mock__',
    companyId: '__tour_mock__',
    name: t('mock.note.category'),
    color: '#6b7280',
    isActive: true,
    isSystem: true,
    createdByUserId: '__tour_mock__',
    updatedByUserId: '__tour_mock__',
  };

  return {
    id: '__tour_mock__',
    createdAt: '2024-03-01T00:00:00.000Z',
    createdBy: '__tour_mock__',
    companyId: '__tour_mock__',
    categoryId: mockCategory.id,
    title: t('mock.note.title'),
    description: t('mock.note.description'),
    shareWithEmployee: true,
    visibility: EmployeeNoteVisibility.EVERYONE,
    isPinned: false,
    createdByUserId: '__tour_mock__',
    updatedByUserId: '__tour_mock__',
    archivedByUserId: null,
    archivedAt: null,
    deleteExpiresAt: '2025-03-01T00:00:00.000Z',
    category: mockCategory,
    tag: mockCategory,
    createdByUser: {
      id: '__tour_mock__',
      firstName: t('mock.note.author_first_name'),
      lastName: t('mock.note.author_last_name'),
    },
    commentCount: 0,
    attachmentCount: 0,
    noteAttachments: [],
    comments: [],
  };
};

export const getMockAnnouncement = (t: TourMockT): Announcement => ({
  id: '__tour_mock__',
  title: t('mock.announcement.title'),
  description: t('mock.announcement.description'),
  status: 'published',
  publishKind: 'immediate',
  publishAt: '2024-03-01T00:00:00.000Z',
  createdAt: '2024-03-01T00:00:00.000Z',
  updatedAt: '2024-03-01T00:00:00.000Z',
  isPinned: false,
  author: t('mock.announcement.author'),
  audience: { type: 'all' },
  attachments: [],
});
